import {
  getCurrencySymbol,
  getDisplayCurrencyCode,
  getLocaleTag,
  LOCALISATION_CONFIG,
} from "@/shared/config/localisation";

type FormatGroupedAmountOptions = {
  locale?: string;
  roundUp?: boolean;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  fallback?: string;
  allowExtractFromText?: boolean;
};

type FormatCurrencyValueOptions = FormatGroupedAmountOptions & {
  inputCurrency?: string;
  withCurrencyCode?: boolean;
  withCurrencySymbol?: boolean;
};

type FormatAmountByConfigOptions = Omit<FormatGroupedAmountOptions, "locale"> & {
  inputCurrency?: string;
  doubleDecimal?: boolean;
};

type FormatAmountOptions = {
  roundUp?: boolean;
  fallback?: string;
};

const EMBEDDED_NUMBER_PATTERN = /-?\d[\d,]*(?:\.\d+)?/;

const resolveAmountLocale = (inputCurrency?: string, localeOverride?: string) => {
  if (localeOverride) {
    return localeOverride;
  }

  const currencyCode = String(inputCurrency || LOCALISATION_CONFIG.currencyCode)
    .trim()
    .toUpperCase();

  if (currencyCode === "INR") {
    return "en-IN";
  }

  return getLocaleTag();
};

export const parseAmountValue = (value: unknown, allowExtractFromText = true): number | null => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value !== "string") {
    return null;
  }

  const normalizedValue = value.trim();
  if (!normalizedValue) {
    return null;
  }

  const directValue = Number(normalizedValue.replace(/,/g, ""));
  if (Number.isFinite(directValue)) {
    return directValue;
  }

  if (!allowExtractFromText) {
    return null;
  }

  const numberMatch = normalizedValue.match(EMBEDDED_NUMBER_PATTERN);
  if (!numberMatch) {
    return null;
  }

  const extractedValue = Number(numberMatch[0].replace(/,/g, ""));
  return Number.isFinite(extractedValue) ? extractedValue : null;
};

export const formatGroupedAmount = (
  value: unknown,
  options: FormatGroupedAmountOptions = {}
): string => {
  const {
    locale = resolveAmountLocale(),
    roundUp = false,
    minimumFractionDigits,
    maximumFractionDigits,
    fallback = "0",
    allowExtractFromText = true,
  } = options;

  const parsedValue = parseAmountValue(value, allowExtractFromText);
  if (parsedValue === null) {
    return fallback;
  }

  const normalizedValue = roundUp ? Math.ceil(parsedValue) : parsedValue;
  const hasDecimal = Math.abs(normalizedValue % 1) > Number.EPSILON;

  const minDigits =
    minimumFractionDigits !== undefined ? minimumFractionDigits : roundUp ? 0 : hasDecimal ? 2 : 0;
  const maxDigits =
    maximumFractionDigits !== undefined ? maximumFractionDigits : roundUp ? 0 : hasDecimal ? 2 : 0;

  return normalizedValue.toLocaleString(locale, {
    minimumFractionDigits: minDigits,
    maximumFractionDigits: maxDigits,
  });
};

export const hasEmbeddedCurrencyText = (value: unknown): boolean => {
  if (typeof value !== "string") {
    return false;
  }

  const trimmedValue = value.trim();
  return /[^\d\s,.-]/.test(trimmedValue) && EMBEDDED_NUMBER_PATTERN.test(trimmedValue);
};

export const replaceEmbeddedAmountWithGrouped = (
  value: string,
  options: Omit<FormatGroupedAmountOptions, "fallback"> = {}
) => {
  const formattedAmount = formatGroupedAmount(value, {
    ...options,
    fallback: value,
  });

  return value.replace(EMBEDDED_NUMBER_PATTERN, formattedAmount);
};

export const formatAmountByConfig = (value: unknown, options: FormatAmountByConfigOptions = {}) => {
  const {
    inputCurrency,
    doubleDecimal = false,
    minimumFractionDigits,
    maximumFractionDigits,
    fallback,
    ...rest
  } = options;

  return formatGroupedAmount(value, {
    locale: resolveAmountLocale(inputCurrency),
    minimumFractionDigits: minimumFractionDigits ?? (doubleDecimal ? 2 : undefined),
    maximumFractionDigits: maximumFractionDigits ?? (doubleDecimal ? 2 : undefined),
    fallback: fallback ?? (doubleDecimal ? "0.00" : undefined),
    ...rest,
  });
};

export const formatCoverCurrencyValue = (value: unknown, currency?: string): string => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  return formatGroupedAmount(value, {
    locale: resolveAmountLocale(currency),
    fallback: String(value),
    allowExtractFromText: false,
  });
};

export const formatCurrencyValue = (
  value: unknown,
  options: FormatCurrencyValueOptions = {}
): string => {
  const {
    inputCurrency,
    withCurrencyCode = true,
    withCurrencySymbol = false,
    fallback = "0.00",
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
    locale,
    ...rest
  } = options;

  const currencyCode = getDisplayCurrencyCode(inputCurrency || LOCALISATION_CONFIG.currencyCode);
  const amountLabel = formatGroupedAmount(value, {
    locale: resolveAmountLocale(currencyCode, locale),
    minimumFractionDigits,
    maximumFractionDigits,
    fallback,
    ...rest,
  });

  if (withCurrencySymbol) {
    return `${getCurrencySymbol(currencyCode)} ${amountLabel}`;
  }

  return withCurrencyCode ? `${currencyCode} ${amountLabel}` : amountLabel;
};
